import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const SELLER = {
  nameEn: "Mohammed Naser Al Hammadi Contracting Co.",
  nameAr: "شركة محمد ناصر الحمادي للمقاولات",
  cr: "1116004323",
  vat: "311128579800003",
  addressEn: "King Fahd Dist., Prince Mashhour Bin Abdulaziz St., Bldg 5207, Ad Duwadimi 17452, KSA",
  phone: "+966 50 033 8215",
  email: "m.ali@mnh-ksa.com",
  website: "www.mnh-ksa.com",
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/invoices/${id}`)
      .then((r) => setInv(r.data))
      .catch(() => setInv(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading invoice…</div>;
  }
  if (!inv) {
    return <div className="text-muted-foreground">Invoice not found.</div>;
  }

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6" data-testid="invoice-detail">
      {/* Print-hide toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to="/admin/invoices">
          <Button variant="ghost" className="rounded-sm" data-testid="inv-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to invoices
          </Button>
        </Link>
        <Button onClick={() => window.print()} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="inv-print-btn">
          <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
        </Button>
      </div>

      {/* Invoice sheet */}
      <div className="mx-auto bg-white text-black shadow-sm border border-border max-w-[860px] print:shadow-none print:border-0" data-testid="invoice-sheet">
        {/* Header band */}
        <div className="bg-secondary text-secondary-foreground px-10 py-7 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Tax Invoice · فاتورة ضريبية</div>
            <div className="mt-2 text-2xl font-black leading-tight">{SELLER.nameEn}</div>
            <div className="text-sm text-secondary-foreground/80" dir="rtl">{SELLER.nameAr}</div>
            <div className="mt-3 text-xs text-secondary-foreground/65 max-w-md leading-relaxed">
              {SELLER.addressEn}
            </div>
            <div className="mt-2 text-xs text-secondary-foreground/65 flex flex-wrap gap-x-4 gap-y-1">
              <span><b>CR:</b> {SELLER.cr}</span>
              <span><b>VAT:</b> {SELLER.vat}</span>
              <span>{SELLER.phone}</span>
              <span>{SELLER.email}</span>
            </div>
          </div>
          <div className="text-end">
            <div className="text-xs uppercase tracking-[0.22em] text-secondary-foreground/65">Invoice #</div>
            <div className="text-2xl font-black text-primary tabular-nums mt-1">{inv.invoice_number}</div>
            <div className="mt-4 text-xs text-secondary-foreground/65">Issue date</div>
            <div className="text-sm font-bold">{inv.issue_date}</div>
            {inv.due_date && <>
              <div className="mt-2 text-xs text-secondary-foreground/65">Due date</div>
              <div className="text-sm font-bold">{inv.due_date}</div>
            </>}
          </div>
        </div>

        {/* Bill To + QR */}
        <div className="px-10 py-7 grid grid-cols-3 gap-6 border-b border-border">
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold">Bill To</div>
            <div className="mt-3 text-lg font-black">{inv.customer_name}</div>
            {inv.customer_vat && <div className="text-xs text-muted-foreground mt-1"><b>VAT:</b> {inv.customer_vat}</div>}
            {inv.customer_address && <div className="text-sm text-muted-foreground mt-2 whitespace-pre-line leading-relaxed">{inv.customer_address}</div>}
            {inv.customer_email && <div className="text-xs text-muted-foreground mt-1">{inv.customer_email}</div>}
            {inv.project_name && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold">Project / Reference</div>
                <div className="text-sm font-semibold mt-1">{inv.project_name}</div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="p-3 bg-white border border-border" data-testid="invoice-qr">
              {inv.qr_data && <QRCodeSVG value={inv.qr_data} size={130} level="M" />}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">ZATCA · Scan to verify</div>
          </div>
        </div>

        {/* Line items */}
        <div className="px-10 py-7">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60 text-[10px] uppercase tracking-[0.18em] font-bold">
                <th className="text-start p-3 w-10">#</th>
                <th className="text-start p-3">Description</th>
                <th className="text-end p-3 w-16">Qty</th>
                <th className="text-end p-3 w-24">Unit Price</th>
                <th className="text-end p-3 w-20">Discount</th>
                <th className="text-end p-3 w-16">VAT %</th>
                <th className="text-end p-3 w-28">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => {
                const line = (it.quantity || 0) * (it.unit_price || 0);
                const taxable = Math.max(line - (it.discount || 0), 0);
                const total = taxable * (1 + (it.tax_rate || 0) / 100);
                return (
                  <tr key={i} className="border-b border-border/60">
                    <td className="p-3 tabular-nums text-muted-foreground">{i + 1}</td>
                    <td className="p-3 font-medium">{it.description}</td>
                    <td className="p-3 text-end tabular-nums">{it.quantity}</td>
                    <td className="p-3 text-end tabular-nums">{fmt(it.unit_price)}</td>
                    <td className="p-3 text-end tabular-nums">{fmt(it.discount)}</td>
                    <td className="p-3 text-end tabular-nums">{it.tax_rate}%</td>
                    <td className="p-3 text-end tabular-nums font-semibold">{fmt(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals + Payment terms */}
        <div className="px-10 pb-7 grid grid-cols-2 gap-8">
          <div className="text-sm text-muted-foreground">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold">Payment Terms</div>
            <div className="mt-2 text-foreground font-medium">{inv.payment_terms || "—"}</div>
            {inv.notes && (
              <>
                <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold">Notes</div>
                <div className="mt-2 whitespace-pre-line leading-relaxed">{inv.notes}</div>
              </>
            )}
          </div>
          <div className="bg-muted/40 p-5 rounded-sm space-y-2">
            <TotalRow label="Subtotal" value={fmt(inv.subtotal)} />
            {inv.discount_total > 0 && <TotalRow label="Discount" value={`-${fmt(inv.discount_total)}`} muted />}
            <TotalRow label="VAT" value={fmt(inv.tax_total)} muted />
            <div className="h-px bg-border my-2" />
            <div className="flex items-center justify-between">
              <span className="text-base font-bold uppercase tracking-[0.16em]">Total</span>
              <span className="text-2xl font-black text-primary tabular-nums">
                {fmt(inv.total)} <span className="text-sm">{inv.currency}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Footer band */}
        <div className="bg-secondary text-secondary-foreground/80 px-10 py-5 text-[11px] flex flex-wrap items-center justify-between gap-2">
          <span>Thank you for your business · شكراً لتعاملكم معنا</span>
          <span className="text-secondary-foreground/55">{SELLER.website} · {SELLER.phone}</span>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .lg\\:flex { display: none !important; }
          aside { display: none !important; }
          main { padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function TotalRow({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${muted ? "text-muted-foreground" : ""}`}>{label}</span>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}
